import * as z from "zod";
import type { BuiltinToolName } from "./builtin-tools.js";
import { Id } from "./ids.js";
import { VIDEO_SIZES, VideoSizeSchema } from "./video.js";

/** The settings dialog's sections, in order. Each surface omits what it cannot offer. */
export const BOT_SETTINGS_DIALOG_SECTIONS = [
  "profile",
  "model",
  "abilities",
  "superBot",
  "notifications",
  "memory",
  "vault",
  "addressBook",
  "conversation",
] as const;
export type BotSettingsDialogSection = (typeof BOT_SETTINGS_DIALOG_SECTIONS)[number];

export const BOT_TITLE_MAX_LENGTH = 80;

/** What a bot has for each setting it has never changed (and what the columns default to). */
export const BOT_SETTINGS_DEFAULTS = {
  contextSize: "standard",
  seeImages: true,
  videoSizes: ["480p", "720p", "1080p"],
  videoApproveEach: false,
  videoClipCap: 2,
  superBot: false,
  superBotActivity: "steady",
  superBotScope: "all",
} as const;

/**
 * How much conversation a run sends. Each size caps the context window the run plans its
 * history against; it never raises a model's own window, and `long` uses the whole window.
 */
export const BOT_CONTEXT_SIZES = ["short", "standard", "long"] as const;
export const BotContextSizeSchema = z.enum(BOT_CONTEXT_SIZES);
export type BotContextSize = z.infer<typeof BotContextSizeSchema>;
export const BOT_CONTEXT_TOKEN_CAPS: Record<BotContextSize, number | null> = {
  short: 32_000,
  standard: 128_000,
  long: null,
};

/** The token cap a bot's context size puts on its runs, or undefined for none. */
export function botContextTokenCap(size: string | null | undefined): number | undefined {
  const parsed = BotContextSizeSchema.safeParse(size);
  return BOT_CONTEXT_TOKEN_CAPS[parsed.success ? parsed.data : "standard"] ?? undefined;
}

/** A saved connection window under a bot's cap; undefined stays undefined (the catalog's). */
export function cappedContextWindow(window: number | undefined, cap: number | undefined) {
  return window !== undefined && cap !== undefined ? Math.min(window, cap) : window;
}

/**
 * Abilities are switches over built-in tools: turning one off adds its tools to the bot's
 * disabled list, which every run already honours. `seeImages` is a column instead, since it
 * changes what the model is sent rather than which tools it gets.
 */
export const BOT_ABILITY_TOOLS = {
  computer: [
    "computer_observe",
    "computer_act",
    "shell",
    "write_file",
    "attach_file",
    "open_path",
    "launch_app",
    "request_takeover",
  ],
  browser: ["browser_navigate", "browser_snapshot", "browser_act"],
  readDocuments: ["read_file", "list_files"],
  makeImages: ["generate_image"],
  makeVideos: ["generate_video", "make_video"],
} as const satisfies Record<string, readonly BuiltinToolName[]>;
export const BotAbilitySchema = z.enum(
  Object.keys(BOT_ABILITY_TOOLS) as [keyof typeof BOT_ABILITY_TOOLS],
);
export type BotAbility = z.infer<typeof BotAbilitySchema>;

/** An ability is on unless any of its tools is disabled. */
export function botAbilityEnabled(disabled: readonly string[], ability: BotAbility): boolean {
  return BOT_ABILITY_TOOLS[ability].every((tool) => !disabled.includes(tool));
}

/** The disabled list with one ability switched, keeping every other entry and its order. */
export function withBotAbility(
  disabled: readonly string[],
  ability: BotAbility,
  enabled: boolean,
): string[] {
  const tools: readonly string[] = BOT_ABILITY_TOOLS[ability];
  const rest = disabled.filter((tool) => !tools.includes(tool));
  return enabled ? rest : [...rest, ...tools];
}

export const BOT_VIDEO_CLIP_CAP_MAX = 20;
export const BotVideoSizesSchema = z
  .array(VideoSizeSchema)
  .min(1)
  .max(VIDEO_SIZES.length)
  .refine((sizes) => new Set(sizes).size === sizes.length, "Each size once");

/** How often a super bot wakes itself; each level is one cron on its managed routine. */
export const SUPER_BOT_ACTIVITIES = ["quiet", "steady", "keen"] as const;
export const SuperBotActivitySchema = z.enum(SUPER_BOT_ACTIVITIES);
export type SuperBotActivity = z.infer<typeof SuperBotActivitySchema>;
export const SUPER_BOT_CRONS: Record<SuperBotActivity, string> = {
  quiet: "0 */4 * * *",
  steady: "0 * * * *",
  keen: "*/15 * * * *",
};

/** A stored activity, reading the level's earlier name ("balanced") as steady. */
export function superBotActivityOf(value: string | null | undefined): SuperBotActivity {
  return SuperBotActivitySchema.catch("steady").parse(value === "balanced" ? "steady" : value);
}

/** Which of the owner's bots in the Space a super bot looks after. */
export const SUPER_BOT_SCOPES = ["all", "created"] as const;
export const SuperBotScopeSchema = z.enum(SUPER_BOT_SCOPES);
export type SuperBotScope = z.infer<typeof SuperBotScopeSchema>;

/**
 * How often a check-in also reads the owner's connected apps (mail, calendar, then any app with a
 * read tool), by activity level. Ticks in between look at the team only.
 */
export const SUPER_BOT_APP_CHECK_EVERY_MS: Record<SuperBotActivity, number> = {
  quiet: 4 * 60 * 60_000,
  steady: 2 * 60 * 60_000,
  keen: 60 * 60_000,
};

/** The title and description a Super bot gets when it has none of its own. */
export const SUPER_BOT_TITLE = "Owner bot";
export function superBotDescription(spaceName: string): string {
  return `Sets up ${spaceName.trim() || "your Space"} with you and keeps it running`;
}

export const SUPER_BOT_ROUTINE_NAME = "Super bot check-in";
/** The check-in's instructions; the server appends the team snapshot when it wakes. */
export const SUPER_BOT_PROMPT = [
  "You lead the bots shared with this Space. Your chat is the Space's team chat: everyone in the Space reads what you write here. The team snapshot below was built for this check-in; work from it instead of looking each bot up.",
  "- Stalled, failed or unanswered work: nudge that bot with message_bot, or hand the work to the bot whose role fits it better. Do not redo their work yourself.",
  "- Character: when a bot's latest output drifts from its title or role, send it a short reminder of its role with message_bot.",
  '- Results: when bots have reported back, combine what they sent into one short summary for the team. If the owner has to decide anything, end with one numbered "Decisions for you" list, highest risk first; refer to decisions already raised by count unless something about them changed.',
  "- Stay silent when there is nothing to report. Never post only to say all is well.",
  "Your tools and approvals are the usual ones: you cannot change another bot's settings, and every approval still applies.",
].join("\n");

/** What a check-in's team snapshot may hold, so a big team still costs one small prompt. */
export const SUPER_BOT_SNAPSHOT_LIMITS = {
  /** Bots read for the snapshot; the rest are only counted. */
  botsRead: 50,
  /** Bots described, those needing attention first. */
  botsShown: 12,
  /** Open work items and tickets named per bot; the rest are counted. */
  itemsPerBot: 3,
  /** Characters of any one quoted line (a title, an error, the bot's latest output). */
  quoteChars: 160,
  /** Characters of the whole snapshot. */
  totalChars: 6000,
} as const;
/** Work that has not moved for this long counts as stalled. */
export const SUPER_BOT_STALLED_AFTER_MS = 60 * 60_000;

const CONTACT_PHONE = /^\+?[0-9][0-9 ().-]{2,31}$/;
export const BotContactFieldsSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    email: z.union([z.literal(""), z.string().trim().toLowerCase().email().max(254)]).default(""),
    phone: z.union([z.literal(""), z.string().trim().regex(CONTACT_PHONE)]).default(""),
    note: z.string().trim().max(1000).default(""),
  })
  .refine((contact) => Boolean(contact.email || contact.phone), {
    message: "Add an email or a phone number",
    path: ["email"],
  });
export const BotContactCreateInput = z.intersection(
  z.object({ botId: Id }),
  BotContactFieldsSchema,
);
export const BotContactUpdateInput = z.intersection(
  z.object({ contactId: Id }),
  BotContactFieldsSchema,
);
export const BotContactSchema = z.object({
  id: Id,
  botId: Id,
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  note: z.string(),
  updatedAt: z.string(),
});
export type BotContact = z.infer<typeof BotContactSchema>;
/**
 * How many messages a bot may send other bots with message_bot: in one run, and per day in its
 * owner's timezone. Null on the bot uses the default; the owner tunes both in bot settings. A
 * Super bot briefs its whole team in one run, so its per-run default is higher. The maximum is
 * the hard ceiling against loops, whatever is saved on the bot.
 */
export const BOT_MESSAGE_LIMIT_DEFAULTS = { perRun: 5, superBotPerRun: 15, perDay: 50 } as const;
export const BOT_MESSAGE_LIMIT_MAX = { perRun: 50, perDay: 1000 } as const;

/** The per-run limit a bot gets when none is saved on it. */
export function defaultBotMessageRunLimit(superBot: boolean | null | undefined): number {
  return superBot ? BOT_MESSAGE_LIMIT_DEFAULTS.superBotPerRun : BOT_MESSAGE_LIMIT_DEFAULTS.perRun;
}
export const BotMessageRunLimitSchema = z.number().int().min(1).max(BOT_MESSAGE_LIMIT_MAX.perRun);
export const BotMessageDailyLimitSchema = z.number().int().min(1).max(BOT_MESSAGE_LIMIT_MAX.perDay);

/** A bot may hold this many contacts. */
export const BOT_CONTACTS_MAX = 500;
